import React, { useState } from "react";
import {
  getFirestore,
  collection,
  doc,
  runTransaction,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import FirebaseApp from "../config/Firebase"; // Adjust path as needed

export default function AddProduct() {
  const [productImages, setProductImages] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [category, setCategory] = useState("DesignerMetal_MetalFrame");
  const [brand, setBrand] = useState("Rayban");
  const [shape, setShape] = useState("Round");
  const [productId, setProductId] = useState("");
  const [material, setMaterial] = useState("");
  const [frameColor, setFrameColor] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cost, setCost] = useState("");
  const [status, setStatus] = useState("Available");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;

    setIsLoading(true);
    setError("");

    try {
      const storage = getStorage(FirebaseApp);
      const newUrls = [];
      const previews = [];

      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          setError("Only image files are allowed.");
          setIsLoading(false);
          return;
        }
        const uniqueName = `${Date.now()}-${file.name}`;
        const storageRef = ref(storage, `products/${uniqueName}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        newUrls.push(url);
        previews.push(URL.createObjectURL(file));
      }

      setImageUrls((prev) => [...prev, ...newUrls]);
      setProductImages((prev) => [...prev, ...previews]);
    } catch (err) {
      setError(`Upload error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    // Validation
    if (
      imageUrls.length === 0 ||
      !productId.trim() ||
      !material.trim() ||
      !frameColor.trim() ||
      !name.trim() ||
      !description.trim() ||
      !cost.trim() ||
      isNaN(parseFloat(cost)) ||
      parseFloat(cost) <= 0
    ) {
      setError("Please complete all fields and upload at least one image with valid cost.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const db = getFirestore(FirebaseApp);
      const safeCategory = category.replace(/&/g, "_");
      const colRef = collection(db, safeCategory);
      const docRef = doc(colRef, productId);

      const data = {
        ProductID: productId,
        Material: material,
        FrameColor: frameColor,
        Name: name,
        Description: description,
        Cost: parseFloat(cost),
        Shape: shape,
        Brand: brand,
        Status: status,
      };
      imageUrls.forEach((url, i) => (data[`ImageUrl${i + 1}`] = url));

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(docRef);
        if (snap.exists()) throw new Error("Product ID already exists.");
        tx.set(docRef, data);
      });

      alert("Product added successfully!");
      // reset
      setProductImages([]);
      setImageUrls([]);
      setProductId("");
      setMaterial("");
      setFrameColor("");
      setName("");
      setDescription("");
      setCost("");
      setStatus("Available");
      setError("");
    } catch (err) {
      setError(`Database error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto my-10 p-8 bg-white rounded-xl shadow-lg">
      <h1 className="text-3xl font-bold text-gray-800 text-center mb-6">Add New Product</h1>

      {error && (
        <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-6 text-center">
          {error}
        </div>
      )}

      {/* Image Preview & Upload */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {productImages.map((src, idx) => (
          <img
            key={idx}
            src={src}
            alt={`Preview ${idx + 1}`}
            className="w-full h-40 object-cover rounded-md border border-gray-200"
          />
        ))}
        <label
          htmlFor="upload"
          className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-gray-400 transition"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            className="w-10 h-10 text-gray-400"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </label>
        <input
          id="upload"
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageUpload}
          className="hidden"
        />
      </div>

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-gray-700 font-medium mb-1">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <option value="DesignerMetal_MetalFrame">Designer Metal & Metal Frame</option>
            <option value="FrameLess">Frame Less</option>
            <option value="FullFrame">Full Frame</option>
            <option value="HalfFrame">Half Frame</option>
            <option value="SafetyGlassess">Safety Glasses</option>
            <option value="Sunglassess">Sunglasses</option>
            <option value="Kids-Frame">Kids Frame</option>
            <option value="Kids-Sunglass">Kids Sunglass</option>
            <option value="Accessories">Accessories</option>
          </select>
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">Product ID</label>
          <input
            type="text"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">Material</label>
          <input
            type="text"
            value={material}
            onChange={(e) => setMaterial(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">Frame Color</label>
          <input
            type="text"
            value={frameColor}
            onChange={(e) => setFrameColor(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">Cost (₹)</label>
          <input
            type="number"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-gray-700 font-medium mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">Shape</label>
          <select
            value={shape}
            onChange={(e) => setShape(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <option>Round</option>
            <option>Oval</option>
            <option>Square</option>
            <option>Rectangular</option>
            <option>Cat-Eye</option>
            <option>Butterfly</option>
            <option>Hexagonal</option>
            <option>Aviator</option>
            <option>Wayfarer</option>
          </select>
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">Brand</label>
          <select
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <option>Generic</option>
            <option>Rayban</option>
            <option>Nova</option>
            <option>Carrera</option>
            <option>Police</option>
            <option>Boss</option>
            <option>Mont blanc</option>
            <option>Tom ford</option>
            <option>Vogue</option>
          </select>
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <option>Available</option>
            <option>Sold</option>
          </select>
        </div>
      </div>

      <button
        onClick={handleAdd}
        disabled={isLoading}
        className="w-full py-3 bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-900 transition"
      >
        {isLoading ? "Adding..." : "Add Product"}
      </button>
    </div>
  );
}
